import { PopoverContent } from '@e2n/ui';
import { faCalendarRange, faUser } from '@fortawesome/pro-regular-svg-icons';
import {
  faCircle,
  faCircleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Location } from '@/domain/mitarbeiter/features/rotationsplan/components/Location';
import { KalenderByMitarbeiterStation } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/entities/kalenderByMitarbeiter/KalenderStation';
import { StyleProps } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/station/types';
import { ArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import { RowDivider } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/RowDivider';

export function MitarbeiterStationIntervalContent({
  interval,
  konfliktStationen,
  onMouseEnter,
  onMouseLeave,
}: {
  interval: KalenderByMitarbeiterStation;
  konfliktStationen: ArbeitsbereichStation[];
  onMouseEnter: (event: React.MouseEvent) => void;
  onMouseLeave: () => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      <div
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className="w-full h-full bg-(--bg-color) border-(--border-color) border border-solid rounded-md flex items-center justify-start px-2"
        style={
          {
            '--bg-color': interval.arbeitsbereich.getFillColor(),
            '--border-color': interval.arbeitsbereich.getBorderColor(),
          } as StyleProps
        }>
        <div className="text-xs truncate">
          {konfliktStationen.length > 0 && (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              className="mr-1 text-primary-red-400"
            />
          )}
          {interval.arbeitsbereich.name}
        </div>
      </div>
      <PopoverContent className="flex flex-col gap-2 p-3 text-sm bg-white w-75">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon
            icon={faCircle}
            color={interval.arbeitsbereich.getBorderColor()}
            size="xs"
          />
          <div className="font-semibold">{interval.arbeitsbereich.name}</div>
        </div>
        <Location standort={interval.arbeitsbereich.standort} />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faCalendarRange} />
            {t('domain.mitarbeiter.rotationsplan.zeitraum')}
          </div>
          <div className="font-semibold">
            {dayjs(interval.station.von).format('DD.MM') +
              ' – ' +
              dayjs(interval.station.bis).format('DD.MM.YYYY')}
          </div>
        </div>
        {interval.station.abteilungsleiter && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faUser} />
              {t('domain.mitarbeiter.rotationsplan.abteilungsleitung')}
            </div>
            <div className="font-semibold">
              {interval.station.abteilungsleiter.vorname +
                ' ' +
                interval.station.abteilungsleiter.nachname}
            </div>
          </div>
        )}
        {konfliktStationen.length > 0 && (
          <>
            <RowDivider />
            <div className="flex flex-col gap-2 text-sm text-neutrals-900">
              <div className="py-1 text-primary-red-400 font-semibold">
                <FontAwesomeIcon icon={faCircleExclamation} />
                <span className="ml-1">
                  {t(
                    'domain.mitarbeiter.rotationsplan.rotationsplaene.konflikt.count',
                    { count: konfliktStationen.length },
                  )}
                </span>
              </div>
              {konfliktStationen.map((station) => (
                <div
                  key={station.id}
                  className="flex items-center justify-between gap-6">
                  <div className="flex items-center min-w-0 gap-2">
                    <FontAwesomeIcon
                      icon={faCircle}
                      color={station.arbeitsbereich.getBorderColor()}
                      size="xs"
                    />
                    <div className="truncate">
                      {station.arbeitsbereich.name}
                    </div>
                  </div>
                  <div className="whitespace-nowrap">
                    {dayjs(station.von).format('DD.MM') +
                      ' - ' +
                      dayjs(station.bis).format('DD.MM.YYYY')}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </PopoverContent>
    </>
  );
}
